import {
  canonical,
  description,
  join,
  meta,
  notFoundAction,
  openGraph,
  permanentRedirectAction,
  renderAction,
  title,
} from '@rea-argonaut/actions';
import { GetAction } from '@property.com.au/argonaut';
import ogImageJpg from '../../assets/og-image.jpg';
import { createUrqlClient, writeUrqlCacheToExchange } from '../../graphql/urqlClient';
import { getSlugFromPathname } from '../../helpers/getSlugFromPathname';
import { fetchStreetPage } from '../graphql/fetchStreetPage';
import { trackView } from '@rea-argonaut/analytics';
import { getCampaignData, getPageData } from '@realestate.com.au/iglu-data';
import { getPcaUserData } from '../../helpers/getPcaUserData';
import { UrlContextProvider } from '../../contexts/UrlContext/UrlContext';
import { Provider as UrqlProvider } from 'urql';
import { StreetPageScreen } from '../screens/StreetPageScreen';
import { getEntryRedirectUrl } from '../../helpers/getEntryRedirectUrl';
import { StreetPageModalContextProvider } from '../contexts/StreetPageModalContext/StreetPageModalContext';

export const getAction: GetAction = async ({ url, user, optimizelyManager }) => {
  const redirectURL = getEntryRedirectUrl(url);
  if (redirectURL) {
    return permanentRedirectAction(redirectURL.href);
  }

  const urqlClient = await createUrqlClient();
  const toggleSuburbCTA = optimizelyManager.isFeatureEnabled('suburb_cta');
  const { data, error } = await fetchStreetPage({
    client: urqlClient,
    slug: getSlugFromPathname(url.pathname),
    toggleSuburbCTA,
  });

  if (error?.networkError) {
    throw Error('Could not successfully connect to Experience API');
  }

  if (!data?.streetPageBySlug) {
    if (error && error.graphQLErrors.length > 0) {
      throw Error(
        'GraphQL error occurred and no street page data was received from Experience API',
      );
    }
    return notFoundAction();
  }

  await writeUrqlCacheToExchange();

  const {
    helmet: { title: pageTitle, description: pageDescription, canonical: pageCanonical, siteName },
  } = data.streetPageBySlug;

  return renderAction(async () => {
    trackView([
      getPageData({
        pageType: 'street',
        siteSection: 'state_suburb_street',
        site: 'pca',
      }),
      getPcaUserData(user),
      getCampaignData(),
    ]);

    return {
      getElements: async () => {
        return {
          Screen: (
            <UrqlProvider value={urqlClient}>
              <UrlContextProvider url={url}>
                <StreetPageModalContextProvider>
                  <StreetPageScreen />
                </StreetPageModalContextProvider>
              </UrlContextProvider>
            </UrqlProvider>
          ),
        };
      },
      getHead: async () =>
        join(
          title(pageTitle),
          description(pageDescription),
          canonical(pageCanonical),
          openGraph({
            property: 'og:locale',
            content: 'en_AU',
          }),
          openGraph({
            property: 'og:type',
            content: 'website',
          }),
          openGraph({
            property: 'og:title',
            content: pageTitle,
          }),
          openGraph({
            property: 'og:description',
            content: pageDescription,
          }),
          openGraph({
            property: 'og:url',
            content: pageCanonical,
          }),
          openGraph({
            property: 'og:site_name',
            content: siteName,
          }),
          openGraph({
            property: 'og:image',
            content: ogImageJpg,
          }),
          meta({
            name: 'robots',
            content: 'index, follow, noodp',
          }),
        ),
    };
  });
};
